import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import Modal from 'react-modal';
import './modal.css';
import { classificarMensagem, isContingencia } from '../../utils/classificarMensagem';
import { useMonitoramento } from '../../context/MonitoramentoContext';
import { playAlertSound } from '../../utils/alertSound';
import { parseServerDate } from '../../utils/serverDate';
import { tipoFaltante } from '../../utils/faltantes';
import { isCertificadoPortal } from '../../utils/certificado';
import ExceptionMessage from './ExceptionMessage';

const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_API_823 || ''}/pdv_exception`,
  auth: { username: 'Portal', password: 'Portal1163' },
});

function formatarData(dataInput) {
  return parseServerDate(dataInput).toLocaleString('pt-BR');
}

function capitalizeFirstLetter(str) {
  return str.toLowerCase().replace(/(^|\s)\S/g, (match) => match.toUpperCase());
}

function ultimaOcorrencia(records) {
  return records.reduce((max, r) => {
    const d = parseServerDate(r.recepcao);
    return d > max ? d : max;
  }, new Date(0));
}

function formatarRelativo(data) {
  if (isNaN(data.getTime()) || data.getTime() === 0) return '';
  const diffMin = Math.floor((Date.now() - data.getTime()) / 60000);
  if (diffMin < 1) return 'agora mesmo';
  if (diffMin < 60) return `há ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `há ${diffH} h`;
  return data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) +
    ' ' + data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function buildSnapshot(records) {
  const map = new Map();
  records.forEach((r) => {
    const t = parseServerDate(r.recepcao).getTime();
    const ordem = classificarMensagem(r.mensagem).ordem;
    const atual = map.get(r.cnpj);
    if (!atual) {
      map.set(r.cnpj, { count: 1, last: t, ordem });
    } else {
      atual.count += 1;
      if (t > atual.last) atual.last = t;
      if (ordem < atual.ordem) atual.ordem = ordem;
    }
  });
  return map;
}

const FILTROS_DATA = [
  { key: null,    label: 'Todos' },
  { key: '2h',    label: 'Últ. 2h' },
  { key: 'hoje',  label: 'Hoje' },
  { key: 'ontem', label: 'Ontem' },
];

const localDateStr = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const applyDateFilter = (records, filtro) => {
  if (!filtro) return records;
  const now          = new Date();
  const todayStr     = localDateStr(now);
  const yesterdayStr = localDateStr(new Date(now - 86400000));
  return records.filter(r => {
    const d = parseServerDate(r.recepcao);
    if (isNaN(d)) return true;
    if (filtro === '2h')    return (now - d) <= 7200000;
    if (filtro === 'hoje')  return localDateStr(d) === todayStr;
    if (filtro === 'ontem') return localDateStr(d) === yesterdayStr;
    return true;
  });
};

const ListaRegistros = ({ filtroNivel }) => {
  const { pdvData, lastUpdate, refresh } = useMonitoramento();

  const [state, setState] = useState({
    erro: false,
    modalIsOpen: false,
    modalData: [],
    ordenacao: 'desc',
    ordenacaoCampo: 'recepcao',
    filtroProjeto: ' Todos',
    ultimosDiasData: null, // { registros: [], nome: '' }
  });
  const [filtroData, setFiltroData] = useState(null);

  const registrosOriginais = useMemo(() =>
    (pdvData || []).filter(r => r.cnpj !== '11111111111111' && !isContingencia(r.mensagem) && !tipoFaltante(r) && !isCertificadoPortal(r)),
    [pdvData]
  );

  const [changedIds, setChangedIds] = useState(() => new Set());
  const [newIds, setNewIds] = useState(() => new Set());
  const prevSnapshotRef = useRef(null);
  const flashTimeoutRef = useRef(null);
  const enterTimeoutRef = useRef(null);

  useEffect(() => {
    const snapshot = buildSnapshot(registrosOriginais);
    const prev = prevSnapshotRef.current;

    if (prev) {
      const changed = new Set();
      const novos = new Set();
      let hasNovaCritica = false;
      snapshot.forEach((info, cnpj) => {
        const antes = prev.get(cnpj);
        if (!antes) novos.add(cnpj);
        if (!antes || info.count > antes.count || info.last > antes.last) {
          changed.add(cnpj);
          if (info.ordem === 1) hasNovaCritica = true;
        }
      });
      if (changed.size > 0) {
        setChangedIds(changed);
        clearTimeout(flashTimeoutRef.current);
        flashTimeoutRef.current = setTimeout(() => setChangedIds(new Set()), 4000);
      }
      if (novos.size > 0) {
        setNewIds(prev => new Set([...prev, ...novos]));
        clearTimeout(enterTimeoutRef.current);
        enterTimeoutRef.current = setTimeout(() => setNewIds(new Set()), 1200);
      }
      if (hasNovaCritica) playAlertSound();
    }

    prevSnapshotRef.current = snapshot;
  }, [registrosOriginais]);

  useEffect(() => () => {
    clearTimeout(flashTimeoutRef.current);
    clearTimeout(enterTimeoutRef.current);
  }, []);

  const projetos = useMemo(() => {
    const unicos = Array.from(new Set(registrosOriginais.map(r => r.projeto)))
      .filter(p => p !== 'Branco' && p !== 'Extra');
    return [' Todos', ...unicos].sort();
  }, [registrosOriginais]);

  const registros = useMemo(() =>
    state.filtroProjeto === ' Todos'
      ? registrosOriginais
      : registrosOriginais.filter(r => r.projeto === state.filtroProjeto),
    [registrosOriginais, state.filtroProjeto]
  );

  const excluirRegistroDoModal = useCallback(async (id) => {
    try {
      await axiosInstance.post(`/${id}`, '');
      setState((prev) => ({
        ...prev,
        modalData: prev.modalData.filter((registro) => registro.controle !== id),
      }));
    } catch (error) {
      console.error('Erro ao excluir o registro:', error);
    }
  }, []);

  const closeModal = useCallback(() => {
    setState((prev) => ({ ...prev, modalIsOpen: false }));
    refresh();
  }, [refresh]);

  const excluirTodos = useCallback(async () => {
    try {
      for (const registro of state.modalData) {
        await excluirRegistroDoModal(registro.controle);
      }
      closeModal();
    } catch (error) {
      console.error('Erro ao excluir todos os registros:', error);
    }
  }, [state.modalData, excluirRegistroDoModal, closeModal]);

  const exibirDetalhes = useCallback((recordsDoCnpj) => {
    const sorted = [...recordsDoCnpj].sort((a, b) =>
      classificarMensagem(a.mensagem).ordem - classificarMensagem(b.mensagem).ordem
    );
    setState((prev) => ({ ...prev, modalData: sorted, modalIsOpen: true }));
  }, []);

  const groupByCnpj = useCallback((records, ordenacaoCampo, ordem) => {
    const groupedData = records.reduce((acc, record) => {
      acc[record.cnpj] = (acc[record.cnpj] || []).concat(record);
      return acc;
    }, {});

    return Object.entries(groupedData)
      .map(([cnpj, recs]) => {
        const classificacoes = recs.map((r) => classificarMensagem(r.mensagem));
        const classificacao = classificacoes.reduce((mais, atual) =>
          atual.ordem < mais.ordem ? atual : mais
        );
        return { cnpj, records: recs, count: recs.length, classificacao };
      })
      .sort((a, b) => {
        if (a.classificacao.ordem !== b.classificacao.ordem) {
          return a.classificacao.ordem - b.classificacao.ordem;
        }
        if (ordenacaoCampo === 'recepcao') {
          return ordem === 'asc'
            ? parseServerDate(a.records[0].recepcao) - parseServerDate(b.records[0].recepcao)
            : parseServerDate(b.records[0].recepcao) - parseServerDate(a.records[0].recepcao);
        }
        return ordem === 'asc' ? a.count - b.count : b.count - a.count;
      });
  }, []);

  const fetchUltimosDias = useCallback(async (cnpj, nome) => {
    try {
      const response = await axiosInstance.get(`/exception/${cnpj}`);
      setState((prev) => ({
        ...prev,
        ultimosDiasData: { registros: response.data, nome },
      }));
    } catch (error) {
      console.error('Erro ao buscar dados dos últimos dias:', error);
    }
  }, []);

  const registrosFiltradosPorData = applyDateFilter(registros, filtroData);
  const sortedRegistros = groupByCnpj(registrosFiltradosPorData, state.ordenacaoCampo, state.ordenacao);
  const visibleRegistros = filtroNivel
    ? sortedRegistros.filter(g => g.classificacao.nivel === filtroNivel)
    : sortedRegistros;

  return (
    <div>
      {state.erro ? (
        <div className="alert alert-danger">Erro ao carregar dados do servidor. Por favor, tente novamente mais tarde.</div>
      ) : (
        <React.Fragment>
          <div className="d-flex align-items-center gap-1 mb-2 flex-wrap">
            {FILTROS_DATA.map(({ key, label }) => (
              <button
                key={String(key)}
                type="button"
                className={`btn btn-sm ${filtroData === key ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setFiltroData(key)}
              >
                {label}
              </button>
            ))}
            <div style={{ width: 1, height: 18, background: '#dee2e6', margin: '0 4px', flexShrink: 0 }} />
            {projetos.map((projeto, index) => {
              const records = projeto === ' Todos'
                ? registrosOriginais
                : registrosOriginais.filter(r => r.projeto === projeto);
              const sevs = records.map(r => classificarMensagem(r.mensagem));
              const piorOrdem = sevs.length > 0 ? Math.min(...sevs.map(s => s.ordem)) : 99;
              const pior = sevs.find(s => s.ordem === piorOrdem);
              const badgeClass = pior ? pior.badgeClass : 'bg-secondary';
              const isActive = state.filtroProjeto === projeto;
              return (
                <button
                  key={index}
                  type="button"
                  className={`btn btn-sm ${isActive ? 'btn-dark' : 'btn-outline-secondary'}`}
                  onClick={() => setState(prev => ({ ...prev, filtroProjeto: projeto }))}
                >
                  {projeto === ' Todos' ? 'Todos os clientes' : capitalizeFirstLetter(projeto)}
                  <span className={`badge ${badgeClass} ms-2`}>{records.length}</span>
                </button>
              );
            })}
            {lastUpdate && (
              <small className="text-muted ms-auto text-nowrap">
                atualizado {lastUpdate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </small>
            )}
          </div>

          <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
            {visibleRegistros.length > 0 ? (
              visibleRegistros.map(({ cnpj, records, count, classificacao }) => (
                <div className="col" key={cnpj}>
                  <div className={`card cs-exception-card cs-sev-${classificacao.sev} h-100${changedIds.has(cnpj) ? ' cs-exception-flash' : ''}${newIds.has(cnpj) ? ' cs-card-enter' : ''}`}>
                    <div className="card-body d-flex flex-column">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h5 className="card-title mb-0">{records[0].nome}</h5>
                        <span className={`badge ${classificacao.badgeClass} rounded-pill fs-6`}>{count}</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="card-subtitle mb-0 text-muted">{cnpj}</h6>
                        <small className="text-muted">{formatarRelativo(ultimaOcorrencia(records))}</small>
                      </div>
                      <p className="card-text text-sm mb-2">
                        <small className="text-muted">{records[0].classe} - {records[0].projeto}</small>
                      </p>
                      <div className={`alert ${classificacao.alertClass} p-2 mb-3`} role="alert">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <strong>Mensagem</strong>
                          <small>{classificacao.tipo} · {classificacao.nivel}</small>
                        </div>
                        <ExceptionMessage mensagem={records[0].mensagem} />
                      </div>
                      <div className="mt-auto d-flex justify-content-end gap-2">
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => exibirDetalhes(records)}>
                          Detalhes
                        </button>
                        <button className="btn btn-sm btn-outline-success" onClick={() => fetchUltimosDias(cnpj, records[0].nome)}>
                          Últimos
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col">
                <div className="alert alert-info" role="alert">
                  Nenhum registro encontrado para o filtro selecionado.
                </div>
              </div>
            )}
          </div>
        </React.Fragment>
      )}

      {/* Modal: Detalhes */}
      <Modal
        isOpen={state.modalIsOpen}
        onRequestClose={closeModal}
        contentLabel='Detalhes'
        className='react-modal-content'
        overlayClassName='react-modal-overlay'
      >
        <div className='modal-header modal-header-wrapper'>
          <h5 className="modal-title">Detalhes dos Registros</h5>
          <button type="button" className="btn-close" aria-label="Fechar" onClick={closeModal}></button>
        </div>
        {state.modalData.length > 0 ? (
          <div className='modal-table'>
            <table className='table table-hover font-sm data-table'>
              <thead className='table-header'>
                <tr>
                  <th>Classe/ID</th>
                  <th>Erro</th>
                  <th>Ação</th>
                </tr>
              </thead>
              <tbody>
                {state.modalData.map((registro, index) => {
                  const classificacao = classificarMensagem(registro.mensagem);
                  const rowClass = classificacao.alertClass.replace('alert-', 'table-');
                  return (
                    <tr key={index} className={rowClass}>
                      <td className='limited-cell'>
                        <p className="fw-bold mb-0">{registro.classe}</p>
                        <small className='text-muted text-nowrap'>{formatarData(registro.recepcao)}</small>
                        <br />
                        <span className={`badge ${classificacao.badgeClass} mt-1`}>{classificacao.tipo} — {classificacao.nivel}</span>
                      </td>
                      <td style={{ width: '100%' }} dangerouslySetInnerHTML={{ __html: registro.mensagem }} />
                      <td>
                        {localStorage.getItem('user-role') === 'MASTER' && (
                          <button className='btn btn-danger btn-sm' onClick={() => excluirRegistroDoModal(registro.controle)}>
                            Excluir
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p>Nenhum registro encontrado.</p>
        )}
        {localStorage.getItem('user-role') === 'MASTER' && state.modalData.length > 0 && (
          <div className="modal-footer justify-content-end">
            <button className='btn btn-danger' onClick={excluirTodos}>
              Excluir Todos
            </button>
          </div>
        )}
      </Modal>

      {/* Modal: Últimos Registros */}
      <Modal
        isOpen={!!state.ultimosDiasData}
        onRequestClose={() => setState((prev) => ({ ...prev, ultimosDiasData: null }))}
        contentLabel='Últimos...'
        className='react-modal-content'
        overlayClassName='react-modal-overlay'
      >
        <div className='modal-header modal-header-wrapper'>
          <h5 className="modal-title">
            Últimos Registros
            {state.ultimosDiasData?.nome && (
              <small className="text-muted ms-2 fw-normal fs-6">— {state.ultimosDiasData.nome}</small>
            )}
          </h5>
          <button
            type="button"
            className="btn-close"
            aria-label="Fechar"
            onClick={() => setState((prev) => ({ ...prev, ultimosDiasData: null }))}
          ></button>
        </div>
        {state.ultimosDiasData ? (
          <div className='modal-table'>
            <table className='table table-hover font-sm data-table'>
              <thead className='table-header'>
                <tr>
                  <th>Classe/ID</th>
                  <th>Mensagem</th>
                  <th>Lido</th>
                </tr>
              </thead>
              <tbody>
                {state.ultimosDiasData.registros.map((registro, index) => (
                  <tr key={index}>
                    <td className='limited-cell'>
                      <p className="fw-bold mb-0">{registro.classe}</p>
                      <small className='text-muted text-nowrap'>{formatarData(registro.recepcao)}</small>
                    </td>
                    <td style={{ width: '75%' }} dangerouslySetInnerHTML={{ __html: registro.mensagem }} />
                    <td>
                      {registro.lido === 'S' && <span className="text-success">&#10003;</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>Nenhum dado encontrado.</p>
        )}
      </Modal>
    </div>
  );
};

export default ListaRegistros;
